import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';
import { X, Plus, Minus, RotateCcw, Loader, Eye, CheckCircle } from 'lucide-react';

/**
 * Support Point - Clickable sphere representing a support point
 */
const SupportPoint = ({ position, id, isRemoved, isAdded, editMode, onToggle }) => {
  const [hovered, setHovered] = useState(false);
  
  // Determine color based on state
  let color = '#3b82f6'; // Blue = auto-detected
  if (isAdded) color = '#22c55e'; // Green = user added
  if (isRemoved) color = '#ef4444'; // Red = marked for removal
  if (hovered) color = editMode === 'add' ? '#22c55e' : '#ef4444';
  
  // Don't render removed points (they'll reappear if user resets)
  if (isRemoved) return null;
  
  return (
    <mesh
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(id);
      }}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <sphereGeometry args={[hovered ? 1.2 : 1, 16, 16]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={hovered ? 0.5 : 0.2}
        transparent
        opacity={0.9}
      />
    </mesh>
  );
};

/**
 * Model mesh component
 */
const ModelMesh = ({ geometry, onClick }) => {
  const meshRef = useRef();
  
  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      onClick={onClick}
    >
      <meshStandardMaterial
        color="#8b8b8b"
        metalness={0.1}
        roughness={0.6}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

/**
 * Click handler for adding points on model surface
 */
const ModelClickHandler = ({ geometry, editMode, onAddPoint }) => {
  const { camera, raycaster, mouse } = useThree();
  const meshRef = useRef();
  
  const handleClick = useCallback((event) => {
    if (editMode !== 'add' || !meshRef.current) return;
    
    // Calculate intersection with model
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObject(meshRef.current);
    
    if (intersects.length > 0) {
      const point = intersects[0].point;
      // Only add points on downward-facing surfaces (potential overhangs)
      const normal = intersects[0].face.normal;
      if (normal.z < -0.3) { // Pointing somewhat downward
        onAddPoint({
          x: point.x,
          y: point.y,
          z: point.z
        });
      }
    }
  }, [editMode, onAddPoint, camera, raycaster, mouse]);
  
  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      onClick={handleClick}
      visible={false}
    >
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
};

/**
 * Scene content
 */
const SceneContent = ({
  geometry,
  points,
  removedPoints,
  addedPoints,
  editMode,
  onTogglePoint,
  onAddPoint
}) => {
  const { camera } = useThree();
  
  // Center camera on model
  useEffect(() => {
    if (geometry) {
      geometry.computeBoundingBox();
      const box = geometry.boundingBox;
      const center = new THREE.Vector3();
      box.getCenter(center);
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      
      camera.position.set(center.x + maxDim, center.y + maxDim, center.z + maxDim * 1.5);
      camera.lookAt(center);
    }
  }, [geometry, camera]);
  
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 10]} intensity={1} />
      <directionalLight position={[-10, -10, 5]} intensity={0.5} />
      
      {geometry && (
        <>
          <ModelMesh geometry={geometry} />
          <ModelClickHandler
            geometry={geometry}
            editMode={editMode}
            onAddPoint={onAddPoint}
          />
        </>
      )}
      
      {/* Render support points */}
      {points.map((pt) => (
        <SupportPoint
          key={pt.id}
          id={pt.id}
          position={[pt.x, pt.y, pt.z]}
          isRemoved={removedPoints.has(pt.id)}
          isAdded={false}
          editMode={editMode}
          onToggle={onTogglePoint}
        />
      ))}
      
      {/* Render added points */}
      {addedPoints.map((pt) => (
        <SupportPoint
          key={pt.id}
          id={pt.id}
          position={[pt.x, pt.y, pt.z]}
          isRemoved={removedPoints.has(pt.id)}
          isAdded={true}
          editMode={editMode}
          onToggle={onTogglePoint}
        />
      ))}
      
      <Grid
        args={[200, 200]}
        position={[0, 0, 0]}
        cellColor="#444"
        sectionColor="#666"
      />
      
      <OrbitControls makeDefault />
    </>
  );
};

/**
 * Support Point Editor Component
 * Allows users to view, add, and remove support points before generation
 */
export const SupportPointEditor = ({
  file,
  treeStyle,
  rotation,
  onClose,
  onApply
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [geometry, setGeometry] = useState(null);
  const [points, setPoints] = useState([]);
  const [removedPoints, setRemovedPoints] = useState(new Set());
  const [addedPoints, setAddedPoints] = useState([]);
  const [editMode, setEditMode] = useState('remove'); // 'add' or 'remove'
  const [generating, setGenerating] = useState(false);
  
  // Load model geometry
  useEffect(() => {
    if (!file) return;
    
    const loader = new STLLoader();
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const geometry = loader.parse(e.target.result);
        geometry.computeVertexNormals();
        
        // IMPORTANT: Apply rotation FIRST (before centering)
        // This matches the backend which applies rotation then centers
        if (rotation && (rotation.x !== 0 || rotation.y !== 0 || rotation.z !== 0)) {
          const rotX = THREE.MathUtils.degToRad(rotation.x || 0);
          const rotY = THREE.MathUtils.degToRad(rotation.y || 0);
          const rotZ = THREE.MathUtils.degToRad(rotation.z || 0);
          geometry.rotateX(rotX);
          geometry.rotateY(rotY);
          geometry.rotateZ(rotZ);
        }
        
        // THEN center geometry (same as backend)
        geometry.computeBoundingBox();
        const center = new THREE.Vector3();
        geometry.boundingBox.getCenter(center);
        const minZ = geometry.boundingBox.min.z;
        geometry.translate(-center.x, -center.y, -minZ);
        
        setGeometry(geometry);
      } catch (err) {
        setError('Failed to load model geometry');
        console.error(err);
      }
    };
    
    reader.readAsArrayBuffer(file);
  }, [file, rotation]);
  
  // Detect support points from backend
  useEffect(() => {
    if (!file) return;
    
    const detectPoints = async () => {
      setLoading(true);
      setError('');
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tree_style', treeStyle || 'forest');
        formData.append('rotation_x', rotation?.x || 0);
        formData.append('rotation_y', rotation?.y || 0);
        formData.append('rotation_z', rotation?.z || 0);
        
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        const response = await fetch(`${backendUrl}/api/supports/detect-points`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Failed to detect support points');
        }
        
        const data = await response.json();
        setPoints(data.points || []);
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    detectPoints();
  }, [file, treeStyle, rotation]);
  
  // Toggle point (remove/restore)
  const handleTogglePoint = useCallback((pointId) => {
    if (editMode === 'remove') {
      setRemovedPoints(prev => {
        const newSet = new Set(prev);
        if (newSet.has(pointId)) {
          newSet.delete(pointId);
        } else {
          newSet.add(pointId);
        }
        return newSet;
      });
    }
    // For added points, remove them completely
    if (addedPoints.some(p => p.id === pointId)) {
      setAddedPoints(prev => prev.filter(p => p.id !== pointId));
    }
  }, [editMode, addedPoints]);
  
  // Add new point
  const handleAddPoint = useCallback((position) => {
    const newPoint = {
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      x: position.x,
      y: position.y,
      z: position.z,
      type: 'custom',
      surface_type: 'external'
    };
    setAddedPoints(prev => [...prev, newPoint]);
  }, []);
  
  // Reset to original points
  const handleReset = () => {
    setRemovedPoints(new Set());
    setAddedPoints([]);
  };
  
  // Calculate final points
  const finalPoints = useMemo(() => {
    const kept = points.filter(p => !removedPoints.has(p.id));
    return [...kept, ...addedPoints];
  }, [points, removedPoints, addedPoints]);
  
  // Apply and generate
  const handleApply = async () => {
    setGenerating(true);
    try {
      await onApply(finalPoints);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" data-testid="support-point-editor">
      <div className="bg-bg-dark rounded-xl w-[95vw] h-[90vh] max-w-7xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-text-muted/20">
          <div>
            <h2 className="text-xl font-bold text-white">Support Point Editor</h2>
            <p className="text-text-secondary text-sm">
              Click points to remove, or switch to Add mode to place new points
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-light rounded-lg transition-colors"
            data-testid="close-editor-btn"
          >
            <X className="w-6 h-6 text-text-secondary" />
          </button>
        </div>
        
        {/* Main content */}
        <div className="flex-1 flex">
          {/* 3D Viewer */}
          <div className="flex-1 relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-bg-dark/80 z-10">
                <div className="text-center">
                  <Loader className="w-10 h-10 text-accent animate-spin mx-auto mb-2" />
                  <p className="text-white">Detecting support points...</p>
                </div>
              </div>
            )}
            
            {error && (
              <div className="absolute top-4 left-4 right-4 bg-red-500/20 border border-red-500/30 p-3 rounded-lg z-10">
                <p className="text-red-400">{error}</p>
              </div>
            )}
            
            <Canvas camera={{ position: [100, 100, 100], fov: 45 }}>
              <SceneContent
                geometry={geometry}
                points={points}
                removedPoints={removedPoints}
                addedPoints={addedPoints}
                editMode={editMode}
                onTogglePoint={handleTogglePoint}
                onAddPoint={handleAddPoint}
              />
            </Canvas>
          </div>
          
          {/* Sidebar */}
          <div className="w-72 border-l border-text-muted/20 p-4 flex flex-col">
            {/* Edit Mode Toggle */}
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Edit Mode</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setEditMode('remove')}
                  className={`px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    editMode === 'remove'
                      ? 'bg-red-500 text-white'
                      : 'bg-bg-light text-text-secondary hover:text-white'
                  }`}
                  data-testid="mode-remove-btn"
                >
                  <Minus className="w-4 h-4" />
                  Remove
                </button>
                <button
                  onClick={() => setEditMode('add')}
                  className={`px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    editMode === 'add'
                      ? 'bg-green-500 text-white'
                      : 'bg-bg-light text-text-secondary hover:text-white'
                  }`}
                  data-testid="mode-add-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
              <p className="mt-2 text-xs text-text-secondary">
                {editMode === 'remove'
                  ? 'Click on blue/green points to remove them'
                  : 'Click on model\'s underside to add new points'}
              </p>
            </div>
            
            {/* Point Statistics */}
            <div className="mb-6 p-4 bg-bg-light rounded-lg">
              <h3 className="text-white font-medium mb-3">Point Statistics</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Original points:</span>
                  <span className="text-white font-medium">{points.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-400">Removed:</span>
                  <span className="text-red-400 font-medium">-{removedPoints.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-400">Added:</span>
                  <span className="text-green-400 font-medium">+{addedPoints.length}</span>
                </div>
                <div className="border-t border-text-muted/20 pt-2 flex justify-between">
                  <span className="text-white font-medium">Final points:</span>
                  <span className="text-accent font-bold">{finalPoints.length}</span>
                </div>
              </div>
            </div>
            
            {/* Legend */}
            <div className="mb-6 p-4 bg-bg-light rounded-lg">
              <h3 className="text-white font-medium mb-3">Legend</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500" />
                  <span className="text-text-secondary">Auto-detected point</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500" />
                  <span className="text-text-secondary">User-added point</span>
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="mt-auto space-y-3">
              <button
                onClick={handleReset}
                className="w-full px-4 py-2 bg-bg-light text-text-secondary hover:text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="reset-points-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Default
              </button>
              
              <button
                onClick={handleApply}
                disabled={generating || finalPoints.length === 0}
                className="w-full px-4 py-3 bg-accent hover:bg-accent-hover text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="apply-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Apply & Generate ({finalPoints.length} points)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPointEditor;
